import { api } from "./client";
import type {
  ExerciseSummary,
  ExerciseDetail,
  InstructorExercise,
  Block,
  BlockKind,
  Chapter,
  Degree,
} from "@/types";

// ── Public ────────────────────────────────────────────────────────────────────

export const listExercises = (params: { course_id: number; year?: string; chapter_id?: number }) => {
  const qs = new URLSearchParams();
  qs.set("course_id", String(params.course_id));
  if (params.year) qs.set("year", params.year);
  if (params.chapter_id) qs.set("chapter_id", String(params.chapter_id));
  return api.get<ExerciseSummary[]>(`/api/exercises/?${qs.toString()}`);
};

export const getExercise = (id: number) =>
  api.get<ExerciseDetail>(`/api/exercises/${id}`);

// test_code n'est pas dans le détail public : récupéré à la validation (authentifié).
export const getStepTest = (stepId: number) =>
  api.get<{ test_code: string }>(`/api/exercises/steps/${stepId}/test`);

// Scaffold caché du prof — récupéré au chargement de l'étape (cache local), composé
// autour du code élève à chaque clic Exécuter (cf. KernelContext.runWithScaffold).
export const getStepScaffold = (stepId: number) =>
  api.get<{ scaffold_pre: string; scaffold_post: string }>(
    `/api/exercises/steps/${stepId}/scaffold`,
  );

// ── Progress ──────────────────────────────────────────────────────────────────

export const completeStep = (stepId: number) =>
  api.post<{ ok: boolean; score: number; hints_used: number }>(
    `/api/progress/steps/${stepId}/complete`,
  );

export const saveSnapshot = (stepId: number, code: string) =>
  api.post<{ ok: boolean }>(`/api/progress/steps/${stepId}/snapshot`, { code });

export const loadSnapshot = (stepId: number) =>
  api.get<{ code: string | null }>(`/api/progress/steps/${stepId}/snapshot`);

// ── Instructor ────────────────────────────────────────────────────────────────

export const listChapters = (courseId: number) =>
  api.get<Chapter[]>(`/api/instructor/chapters?course_id=${courseId}`);

export const createChapter = (courseId: number, title: string) =>
  api.post<Chapter>("/api/instructor/chapters", { title, course_id: courseId });

export const updateChapter = (id: number, data: { title?: string; visible?: boolean }) =>
  api.patch<Chapter>(`/api/instructor/chapters/${id}`, data);

export const deleteChapter = (id: number) =>
  api.delete<{ ok: boolean }>(`/api/instructor/chapters/${id}`);

export const reorderChapters = (courseId: number, order: number[]) =>
  api.post<{ ok: boolean }>("/api/instructor/chapters/reorder", { order, course_id: courseId });

// ── Degrés / années (instructor) ───────────────────────────────────────────────

export const listDegrees = (courseId: number) =>
  api.get<Degree[]>(`/api/instructor/degrees?course_id=${courseId}`);

export const createDegree = (courseId: number, code: string, label: string) =>
  api.post<Degree>("/api/instructor/degrees", { code, label, course_id: courseId });

export const updateDegree = (id: number, data: { code?: string; label?: string; order?: number }) =>
  api.patch<Degree>(`/api/instructor/degrees/${id}`, data);

export const deleteDegree = (id: number) =>
  api.delete<{ ok: boolean }>(`/api/instructor/degrees/${id}`);

export const reorderDegrees = (courseId: number, order: number[]) =>
  api.post<{ ok: boolean }>("/api/instructor/degrees/reorder", { order, course_id: courseId });

export const listInstructorExercises = (courseId: number) =>
  api.get<InstructorExercise[]>(`/api/instructor/exercises?course_id=${courseId}`);

export const getInstructorExercise = (id: number) =>
  api.get<InstructorExercise>(`/api/instructor/exercises/${id}`);

export const createExercise = (data: Partial<InstructorExercise> & { course_id: number }) =>
  api.post<{ id: number; title: string }>("/api/instructor/exercises", data);

export const updateExercise = (id: number, data: Partial<InstructorExercise>) =>
  api.patch<{ id: number; title: string; published: boolean }>(`/api/instructor/exercises/${id}`, data);

export const deleteExercise = (id: number) =>
  api.delete<{ ok: boolean }>(`/api/instructor/exercises/${id}`);

export const duplicateExercise = (id: number) =>
  api.post<{ id: number; title: string }>(`/api/instructor/exercises/${id}/duplicate`);

// Copie un exercice (de n'importe quel cours) vers un de mes cours (brouillon).
export const copyExerciseToMine = (exerciseId: number, toCourseId: number) =>
  api.post<{ id: number; title: string }>(
    `/api/instructor/exercises/${exerciseId}/copy-to-mine`, { to_course_id: toCourseId },
  );

export const reorderExercises = (courseId: number, order: number[]) =>
  api.post<{ ok: boolean }>("/api/instructor/exercises/reorder", { order, course_id: courseId });

export const createStep = (exerciseId: number, data: object) =>
  api.post<{ id: number; order: number; title: string }>(
    `/api/instructor/exercises/${exerciseId}/steps`,
    data,
  );

export const updateStep = (stepId: number, data: object) =>
  api.patch<{ id: number; order: number; title: string }>(
    `/api/instructor/steps/${stepId}`,
    data,
  );

export const deleteStep = (stepId: number) =>
  api.delete<{ ok: boolean }>(`/api/instructor/steps/${stepId}`);

export const reorderSteps = (exerciseId: number, order: number[]) =>
  api.post<{ ok: boolean }>(
    `/api/instructor/exercises/${exerciseId}/steps/reorder`,
    { order },
  );

export const duplicateStep = (stepId: number) =>
  api.post<{ id: number; order: number; title: string }>(
    `/api/instructor/steps/${stepId}/duplicate`,
  );

// ── Blocs de consigne ──────────────────────────────────────────────────────────

export const createBlock = (stepId: number, data: { kind: BlockKind; text?: string }) =>
  api.post<Block>(`/api/instructor/steps/${stepId}/blocks`, data);

// Upload image/fichier → crée le fichier + le bloc (type déduit du mime).
export const uploadBlock = (stepId: number, file: File) =>
  api.upload<Block>(`/api/instructor/steps/${stepId}/blocks/upload`, file);

export const updateBlock = (
  blockId: number,
  data: { text?: string; caption?: string; alt?: string; order?: number },
) => api.patch<Block>(`/api/instructor/blocks/${blockId}`, data);

export const deleteBlock = (blockId: number) =>
  api.delete<{ ok: boolean }>(`/api/instructor/blocks/${blockId}`);

export const reorderBlocks = (stepId: number, order: number[]) =>
  api.post<{ ok: boolean }>(`/api/instructor/steps/${stepId}/blocks/reorder`, { order });
